import React, { useState, useEffect } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import DatePicker from 'react-datepicker';
import { Card, CardContent } from '@/components/ui/card';
import { Heading } from '@/components/ui/typography';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Stack } from '@/components/ui/layout';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

function Education({ onUpdate, existingEducation = [] }) {
  const [educationList, setEducationList] = useState(existingEducation || []);

  useEffect(() => {
    onUpdate(educationList);
  }, [educationList]);

  const addEducation = () => {
    setEducationList([
      ...educationList,
      { school: '', degree: '', fieldOfStudy: '', grade: '', startDate: null, endDate: null }
    ]);
  };

  const handleChange = (index, event) => {
    const { id, value } = event.target;
    const updatedEducation = [...educationList];
    // Extract field name from id by removing the index suffix (e.g., "school0" -> "school", "fieldOfStudy0" -> "fieldOfStudy")
    const fieldName = id.replace(new RegExp(`${index}$`), '');
    updatedEducation[index][fieldName] = value;
    setEducationList(updatedEducation);
  };

  const handleDateChange = (index, date, field) => {
    const updatedEducation = [...educationList];
    updatedEducation[index][field] = date;
    setEducationList(updatedEducation);
  };

  const deleteEducation = (index) => {
    const updatedEducation = educationList.filter((_, i) => i !== index);
    setEducationList(updatedEducation);
  };

  return (
    <Card className="mb-6 bg-white">
      <CardContent className="p-4">
        <Heading level="h2" className="text-base font-semibold mb-3">
          Education
          <span className="text-destructive ml-1">*</span>
        </Heading>
        <Stack spacing="md">
          {educationList.map((education, index) => (
            <div key={index} className={cn("space-y-4 p-4 border rounded-lg")}>
              <div className={cn("grid grid-cols-2 gap-4")}>
                <div>
                  <Label htmlFor={`school${index}`} className="text-sm font-medium mb-1.5">School</Label>
                  <Input
                    type="text"
                    id={`school${index}`}
                    value={education.school}
                    onChange={(e) => handleChange(index, e)}
                    placeholder="Ex. University Name"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor={`degree${index}`} className="text-sm font-medium mb-1.5">Degree</Label>
                  <Input
                    type="text"
                    id={`degree${index}`}
                    value={education.degree}
                    onChange={(e) => handleChange(index, e)}
                    placeholder="Ex. Bachelor's"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor={`fieldOfStudy${index}`} className="text-sm font-medium mb-1.5">Field of Study</Label>
                  <Input
                    type="text"
                    id={`fieldOfStudy${index}`}
                    value={education.fieldOfStudy}
                    onChange={(e) => handleChange(index, e)}
                    placeholder="Ex. Computer Science"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor={`grade${index}`} className="text-sm font-medium mb-1.5">Grade/Percentage</Label>
                  <Input
                    type="text"
                    id={`grade${index}`}
                    value={education.grade}
                    onChange={(e) => handleChange(index, e)}
                    placeholder="Ex. 7.8 CGPA"
                    className="mt-1"
                  />
                </div>
              </div>
              <div className={cn("grid grid-cols-2 gap-4")}>
                <div>
                  <Label htmlFor={`startDate${index}`} className="text-sm font-medium mb-1.5">Start Date</Label>
                  <DatePicker
                    selected={education.startDate ? new Date(education.startDate) : null}
                    onChange={(date) => handleDateChange(index, date, 'startDate')}
                    dateFormat="MM/yyyy"
                    showMonthYearPicker
                    className={cn("w-full h-9 mt-1 px-3 py-1 text-sm border border-input rounded-md bg-background")}
                    placeholderText="Select Start Date"
                  />
                </div>
                <div>
                  <Label htmlFor={`endDate${index}`} className="text-sm font-medium mb-1.5">End Date</Label>
                  <DatePicker
                    selected={education.endDate ? new Date(education.endDate) : null}
                    onChange={(date) => handleDateChange(index, date, 'endDate')}
                    dateFormat="MM/yyyy"
                    showMonthYearPicker
                    className={cn("w-full h-9 mt-1 px-3 py-1 text-sm border border-input rounded-md bg-background")}
                    placeholderText="Select End Date"
                  />
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => deleteEducation(index)}
                className="text-destructive self-end"
              >
                <X className="w-4 h-4 mr-2" />
                Delete Education
              </Button>
            </div>
          ))}
          <Button variant="outline" onClick={addEducation} className="w-full">
            Add Education
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default Education;